import React from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldAlertIcon } from 'lucide-react';
import { useRestaurantAccessStatus, useIsPlatformAdmin, isRestaurantActive } from '../../lib/access-status';
import { SuspensionScreen } from './SuspensionScreen';
interface RestaurantAccessGateProps {
  /**
   * Id real del restaurante actual, resuelto del contexto de autenticación.
   * NUNCA se compara contra un id fijo: la condición depende exclusivamente
   * de lo que diga `restaurants.access_status` para ESTE id.
   */
  restaurantId: string;
  restaurantName?: string;
  /**
   * Id del usuario autenticado. Se usa únicamente para resolver, con el
   * mecanismo de plataforma ya existente, si corresponde la excepción de
   * soporte. Nunca se compara contra ningún id, correo o nombre concreto.
   */
  userId?: string;
  /**
   * Cuando es false, el gate no verifica nada y renderiza los hijos tal
   * cual. Se usa para dejar fuera las rutas públicas del cliente y la
   * pantalla de inicio de sesión.
   */
  enabled: boolean;
  children: React.ReactNode;
}

/** Estado de carga neutro — sin datos operativos ni marca de restaurante. */
function CheckingAccessScreen() {
  return <div className="min-h-screen bg-brand-offwhite flex flex-col items-center justify-center p-6" data-id="element-1754" data-mp="252q4xiukulpn">
      <div className="w-12 h-12 bg-brand-charcoal rounded-xl flex items-center justify-center mb-6" data-id="element-1755" data-mp="295csh7k7tkh1">
        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1756" data-mp="2e4fu0i0zl8k9" />
      </div>
      <p className="text-sm text-gray-500" data-id="element-1757" data-mp="2j1zazpp2jzfw">Verificando acceso...</p>
    </div>;
}

/**
 * Control de acceso administrativo por restaurante.
 *
 * Punto ÚNICO de verificación: se monta dentro de `TenantProvider`, que es
 * el lugar más alto del routing donde ya está resuelto el restaurante real
 * del usuario autenticado. Cubre a la vez el árbol `/admin/:slug/...` y el
 * árbol de subdominio, sin duplicar la consulta en cada página.
 *
 * Mientras el restaurante esté suspendido, el árbol operativo NO se
 * renderiza: no hay dashboard, sidebar, mesas, caja, pedidos, KDS,
 * inventario, reportes, configuración ni información financiera detrás de
 * la pantalla de suspensión.
 *
 * No escribe en Supabase ni modifica RPCs, RLS, pagos, sesiones ni datos.
 */
export function RestaurantAccessGate({
  restaurantId,
  restaurantName,
  userId,
  enabled,
  children
}: RestaurantAccessGateProps) {
  const location = useLocation();
  // La ruta activa participa como llave de revalidación: al navegar entre
  // pantallas operativas el estado se vuelve a comprobar, así que no se
  // puede saltar el bloqueo escribiendo otra ruta interna a mano.
  const {
    status,
    loading
  } = useRestaurantAccessStatus(enabled ? restaurantId : undefined, enabled ? location.pathname : undefined);
  // Excepción de soporte: rol REAL de plataforma, resuelto con el
  // mecanismo existente de NOIZA. Un usuario normal del restaurante nunca
  // obtiene `true` acá, porque la verificación consulta la tabla de
  // administradores de plataforma y no el rol dentro del restaurante.
  const {
    isPlatformAdmin,
    loading: platformLoading
  } = useIsPlatformAdmin(enabled ? userId : undefined);
  if (!enabled) {
    return <>{children}</>;
  }

  // Estado de carga: nunca se renderiza el panel antes de conocer el
  // estado, para evitar un destello de contenido protegido.
  if (loading || platformLoading) {
    return <CheckingAccessScreen data-id="element-1758" data-mp="1ny3droa5mus9" />;
  }
  const active = isRestaurantActive(status);
  if (!active && !isPlatformAdmin) {
    return <SuspensionScreen status={status} restaurantName={restaurantName} data-id="element-1759" data-mp="1swoerpx8dihr" />;
  }

  // Restaurante suspendido + super admin de plataforma: se permite el
  // acceso para soporte y diagnóstico, con un aviso discreto que deja
  // claro que este restaurante NO está operativo para su equipo.
  if (!active) {
    return <>
        <div role="status" className="sticky top-0 z-50 flex items-center justify-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-300 text-amber-900 text-sm font-medium" data-id="element-1760" data-mp="3decid8hl7jkz">
          <ShieldAlertIcon className="w-4 h-4 shrink-0" aria-hidden="true" data-id="element-1761" data-mp="2joc6lkwdkg0a" />
          <span data-id="element-1762" data-mp="2epsf74z0ic0j">
            <strong className="font-semibold" data-id="element-1763" data-mp="2ep98yliq5c2c">Restaurante suspendido</strong>
            {status?.suspension_reason === 'payment_pending' ? ' — Pago pendiente' : status?.access_status ? ` — ${status.access_status}` : ''}
            . Acceso habilitado solo para administración de plataforma.
          </span>
        </div>
        {children}
      </>;
  }
  return <>{children}</>;
}